<template>
  <div class="btc-tree-wrap">
    <svg ref="svg" :width="width" :height="height"></svg>
  </div>
</template>

<script>
import * as d3 from 'd3';

const NODE_H = 46; // vertical spacing per node — leaves need room for a 2-line label
const LEVEL_W = 260; // horizontal spacing per tree depth level
const MARGIN = 20;

export default {
  name: 'BtcAddressTree',
  props: {
    data: { type: Object, required: true }, // { name, children: [...] }
    accentColor: { type: String, default: '#F7931A' }, // brand color for nodes/heatmap (defaults to BTC orange)
    unit: { type: String, default: 'BTC' }, // balance suffix shown on leaf nodes
    decimals: { type: Number, default: 8 }, // balance precision shown on leaf nodes
  },
  data() {
    return { width: 900, height: 400 };
  },
  watch: {
    data: {
      deep: true,
      handler() { this.render(); },
    },
  },
  mounted() {
    this.render();
  },
  methods: {
    render() {
      const svg = d3.select(this.$refs.svg);
      svg.selectAll('*').remove();

      const root = d3.hierarchy(this.data);
      // nodeSize (rather than size) gives every node the same fixed spacing,
      // so sparse branches don't get squeezed and dense ones don't overlap.
      const treeLayout = d3.tree().nodeSize([NODE_H, LEVEL_W]);
      treeLayout(root);

      const descendants = root.descendants();
      const minX = Math.min(...descendants.map(d => d.x));
      const maxX = Math.max(...descendants.map(d => d.x));
      // Leaf labels extend ~230px to the right of their node for the address/balance line.
      const maxY = Math.max(...descendants.map(d => d.y)) + 230;

      const maxBalance = Math.max(0, ...descendants.map(d => d.data.balance || 0));
      // Square-root scale so a handful of small balances still show visible
      // color contrast instead of being drowned out by one large balance.
      const heat = d3.scaleSqrt().domain([0, maxBalance || 1]).range([0, 1]).clamp(true);
      // Interpolate from white to the chain's brand color, capped at 0.6 mix so
      // the background never gets dark enough to hurt text legibility.
      const heatScale = d3.interpolateRgb('#ffffff', this.accentColor);
      const colorFor = balance => (balance > 0 ? heatScale(0.12 + heat(balance) * 0.48) : '#eee');

      this.height = maxX - minX + MARGIN * 2;
      this.width = maxY + MARGIN * 2;

      const g = svg.append('g').attr('transform', `translate(${MARGIN},${MARGIN - minX})`);

      g.selectAll('.link')
        .data(root.links())
        .join('path')
        .attr('class', 'btc-tree-link')
        .attr('d', d3.linkHorizontal().x(d => d.y).y(d => d.x));

      const node = g.selectAll('.node')
        .data(descendants)
        .join('g')
        .attr('class', d => 'btc-tree-node' + (d.data.isActive ? ' active' : ''))
        .attr('transform', d => `translate(${d.y},${d.x})`)
        .style('cursor', d => (d.data.addressIndex !== undefined ? 'pointer' : 'default'))
        .on('click', (event, d) => {
          if (d.data.addressIndex !== undefined) {
            this.$emit('select', { fingerprint: d.data.fingerprint, group: d.data.group, addressIndex: d.data.addressIndex });
          }
        });

      node.filter(d => d.data.address)
        .append('rect')
        .attr('class', 'btc-tree-heat')
        .attr('x', 4)
        .attr('y', -12)
        .attr('width', 218)
        .attr('height', 26)
        .attr('rx', 4)
        .attr('fill', d => colorFor(d.data.balance || 0));

      node.append('circle').attr('r', 5).attr('fill', this.accentColor);

      node.append('text')
        .attr('class', 'btc-tree-label')
        .attr('dy', '0.32em')
        .attr('x', d => (d.children ? -9 : 9))
        .attr('text-anchor', d => (d.children ? 'end' : 'start'))
        .text(d => d.data.name);

      node.filter(d => d.data.address)
        .append('text')
        .attr('class', 'btc-tree-sub')
        .attr('dy', '1.4em')
        .attr('x', 9)
        .attr('text-anchor', 'start')
        .text(d => {
          const addr = `${d.data.address.slice(0, 8)}…${d.data.address.slice(-6)}`;
          if (d.data.loading) return `${addr} — …`;
          return `${addr} — ${(d.data.balance || 0).toFixed(this.decimals)} ${this.unit}`;
        });
    },
  },
};
</script>

<style scoped>
.btc-tree-wrap { width: 100%; overflow-x: auto; }
.btc-tree-link {
  fill: none;
  stroke: #ccc;
  stroke-width: 1.5px;
}
.btc-tree-node circle { stroke: #fff; stroke-width: 1px; }
.btc-tree-heat { stroke: rgba(0,0,0,0.08); stroke-width: 1px; }
.btc-tree-node .btc-tree-label { font-size: 12px; fill: currentColor; }
.btc-tree-node .btc-tree-sub { font-size: 10px; fill: #4a3a1f; font-family: monospace; }
.btc-tree-node.active circle { r: 7; }
.btc-tree-node.active .btc-tree-label { font-weight: 700; }
</style>
